import { Box, Typography } from '@mui/material';
import { PlanDetailsInterface } from '../../../../Models/Seller';

interface PremiumPlanBoxProps {
    planDetails: PlanDetailsInterface;
    onClick: () => void;
}

export const PremiumPlanBox = ({ planDetails, onClick }: PremiumPlanBoxProps) => {
    const formatDate = (dateString: string | Date) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const isPlanActive = new Date(planDetails.expiryTimeStamp) >= new Date();
    
    // Get plan ID - handle both string and object cases
    const planId = typeof planDetails.plan === 'string' 
        ? planDetails.plan 
        : planDetails.plan?._id || planDetails.plan?.id || 'N/A';

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                mx: 2,
                mb: 2,
                p: 1.5,
                background: isPlanActive
                    ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                    : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                borderRadius: 2,
                border: isPlanActive ? '1.5px solid #f59e0b' : '1.5px solid #ef4444',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: isPlanActive 
                        ? '0 4px 12px rgba(245, 158, 11, 0.3)'
                        : '0 4px 12px rgba(239, 68, 68, 0.3)'
                }
            }}
        >
            {/* Diagonal Ribbon */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: -30,
                    transform: 'rotate(45deg)',
                    width: '100px',
                    textAlign: 'center',
                    py: 0.5,
                    background: isPlanActive 
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    zIndex: 1
                }}
            >
                <Typography sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {isPlanActive ? 'Active' : 'Expired'}
                </Typography>
            </Box>

            {/* Plan ID */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography sx={{ fontSize: '1rem' }}>
                    👑
                </Typography>
                <Box sx={{
                    px: 1,
                    py: 0.25,
                    background: isPlanActive 
                        ? 'rgba(255, 255, 255, 0.6)'
                        : 'rgba(255, 255, 255, 0.5)',
                    borderRadius: 0.75,
                    fontFamily: "'Monaco', 'Courier New', monospace",
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: isPlanActive ? '#92400e' : '#991b1b',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 'calc(100% - 24px)'
                }}>
                    {planId.substring(0, 12)}
                </Box>
            </Box>

            {/* Expiry Date */}
            <Typography sx={{
                fontSize: '0.7rem',
                color: isPlanActive ? '#78350f' : '#7f1d1d',
                fontStyle: 'italic'
            }}>
                {isPlanActive ? 'Expires' : 'Expired'} on {formatDate(planDetails.expiryTimeStamp)}
            </Typography>
        </Box>
    );
};
